import React, { forwardRef, useCallback } from 'react';
import { Link, LinkProps } from 'react-router';

export interface IHashLinkProps extends LinkProps {
    smooth?: boolean;
}

// How long to keep watching the DOM for a hash target that has not rendered yet.
const OBSERVE_TIMEOUT_MS = 5000;

const getHash = (to: LinkProps['to']): string => {
    if (typeof to === 'string') {
        const index = to.indexOf('#');

        return index === -1 ? '' : to.slice(index);
    }

    return to.hash ?? '';
};

/**
 * Scrolls to the element the hash points at. The target may not be in the DOM yet
 * (lazy-loaded route chunk), so fall back to watching for mutations for a short window.
 */
const scrollToHash = (hash: string, smooth?: boolean) => {
    const id = hash.replace(/^#/, '');

    if (!id) {
        return;
    }

    const scrollIfPresent = () => {
        const target = id === 'top' ? document.body : document.getElementById(id);

        if (!target) {
            return false;
        }

        target.scrollIntoView(smooth ? { behavior: 'smooth' } : undefined);

        return true;
    };

    if (scrollIfPresent()) {
        return;
    }

    const observer = new MutationObserver(() => {
        if (scrollIfPresent()) {
            observer.disconnect();
            window.clearTimeout(timeoutId);
        }
    });

    observer.observe(document, { childList: true, subtree: true });

    const timeoutId = window.setTimeout(() => observer.disconnect(), OBSERVE_TIMEOUT_MS);
};

/**
 * Drop-in replacement for the `HashLink` previously provided by react-router-hash-link,
 * which is unmaintained and depends on the deprecated react-router-dom package.
 */
const HashLink = forwardRef<HTMLAnchorElement, IHashLinkProps>(({ smooth, onClick, to, ...rest }, ref) => {
    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            onClick?.(event);

            // Let modified clicks (new tab, download, etc.) behave natively.
            if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey) {
                return;
            }

            // Defer so react-router has committed the navigation first.
            window.setTimeout(() => scrollToHash(getHash(to), smooth), 0);
        },
        [onClick, to, smooth],
    );

    return <Link {...rest} to={to} ref={ref} onClick={handleClick} />;
});

HashLink.displayName = 'HashLink';

export default HashLink;
